import React, { useRef, useState, useMemo } from "react";

/**
 * Utilitário para concatenar campos de um objeto conforme concatFields.
 * Se concatFields não for informado ou vazio, retorna apenas o campo nameField.
 * @param {object} item - O objeto do dado.
 * @param {string[]} concatFields - Array de nomes de campos a concatenar.
 * @param {string} nameField - Campo padrão para exibição.
 * @returns {string}
 */
export function getDisplayValue(item, concatFields = [], nameField = "nome") {
    if (Array.isArray(concatFields) && concatFields.length > 0) {
        return concatFields
            .map(field => (item[field] != null ? item[field] : ""))
            .filter(val => val !== "")
            .join(" - ");
    }
    return item[nameField] || "";
}

const ROW_HEIGHT = 32; // px, altura aproximada de cada linha da tabela
const HEADER_HEIGHT = 32; // px, altura do cabeçalho reduzida para igualar à linha

const clearButtonStyle = {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "12px", // reduzido de 15px para 12px
    color: "#888",
    padding: 0,
    zIndex: 10,
    lineHeight: 1,
    height: "16px", // reduzido de 20px para 16px
    width: "16px",  // reduzido de 20px para 16px
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};

const MultiSelect = ({
    data = [],
    selected = [],
    setSelected,
    label = "Itens",
    idField = "id",
    nameField = "nome",
    visibleLines = 6,
    fieldLength = 100,
    emptyText = "Nenhum item encontrado.",
    concatFields = [],
}) => {
    const tableContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const [search, setSearch] = useState("");
    const [showOnlyMarked, setShowOnlyMarked] = useState(false);

    // Filtra os dados conforme o texto digitado no campo de pesquisa (case-insensitive)
    const filteredData = useMemo(() => {
        let result = data;
        if (search) {
            const searchLower = search.toLowerCase();
            result = result.filter(item => {
                // Pesquisa deve considerar concatFields se fornecido
                const displayValue = getDisplayValue(item, concatFields, nameField);
                return displayValue.toString().toLowerCase().includes(searchLower);
            });
        }
        if (showOnlyMarked) {
            result = result.filter(item => selected.includes(item[idField]));
        }
        return result;
    }, [data, search, nameField, showOnlyMarked, selected, concatFields]);

    // Altura fixa baseada em visibleLines (linhas visíveis) + cabeçalho
    const fixedHeight = HEADER_HEIGHT + (ROW_HEIGHT * visibleLines);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelected(filteredData.map((item) => item[idField]));
        } else {
            setSelected([]);
        }
    };

    const handleSelectItem = (itemId, checked) => {
        if (checked) {
            setSelected((prev) => [...prev, itemId]);
        } else {
            setSelected((prev) => prev.filter((id) => id !== itemId));
        }
    };

    const allSelected = filteredData.length > 0 && filteredData.every((item) => selected.includes(item[idField]));

    // Função para alternar cor de fundo das linhas (zebrado)
    const getRowStyle = (index) => ({
        backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#fff"
    });

    // Se o número de linhas filtradas for menor que visibleLines, o fundo do componente deve ser cinza claro
    const shouldGrayBackground = filteredData.length < visibleLines;

    // Novo: ao clicar na linha, marcar/desmarcar o checkbox correspondente
    const handleRowClick = (itemId, isChecked) => {
        handleSelectItem(itemId, !isChecked);
    };

    // Função para limpar o input de pesquisa e focar novamente no input
    const handleClearSearch = () => {
        setSearch("");
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    return (
        <div className="col-md-12">
            <label className="m-0 font-90">{label}</label>
            <div
                ref={tableContainerRef}
                style={{
                    border: "1px solid #ced4da",
                    borderRadius: "0.25rem",
                    minHeight: `${fixedHeight}px`,
                    maxHeight: `${fixedHeight}px`,
                    overflowY: "auto",
                    background: shouldGrayBackground ? "#f1f3f4" : "#fff",
                    transition: "background 0.2s"
                }}
            >
                <table
                    className="table table-sm mb-0"
                    style={{
                        width: "100%",
                        background: "transparent",
                        fontSize: "12px",
                        lineHeight: "1.1",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                    }}
                >
                    <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                        <tr>
                            <th style={{ width: "40px", padding: "0 8px", height: `${HEADER_HEIGHT}px`, borderBottom: "1px solid #dee2e6", verticalAlign: "middle" }}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={handleSelectAll}
                                    style={{ margin: 0, height: "16px" }}
                                />
                            </th>
                            <th style={{ padding: "0 8px", height: `${HEADER_HEIGHT}px`, borderBottom: "1px solid #dee2e6", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                                    <span style={{ flex: "0 0 auto" }}>Nome</span>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1, marginLeft: 12, justifyContent: "flex-end" }}>
                                        <div style={{ position: "relative", width: "220px", minWidth: "120px", maxWidth: "260px", display: "flex", alignItems: "center" }}>
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Pesquisar..."
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                                style={{
                                                    fontSize: "12px",
                                                    height: "28px",
                                                    padding: "0 28px 0 8px", // espaço para o X à direita
                                                    border: "1px solid #ced4da",
                                                    borderRadius: "0.25rem",
                                                    width: "100%",
                                                    boxSizing: "border-box"
                                                }}
                                            />
                                            {/* Botão X para limpar o campo de pesquisa */}
                                            {search && (
                                                <button
                                                    type="button"
                                                    aria-label="Limpar pesquisa"
                                                    style={clearButtonStyle}
                                                    onClick={handleClearSearch}
                                                    tabIndex={0}
                                                >
                                                    {/* Ícone X SVG para melhor acessibilidade */}
                                                    <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                                                        <line x1="3" y1="3" x2="11" y2="11" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                                                        <line x1="11" y1="3" x2="3" y2="11" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                marginLeft: 18,
                                                minWidth: "110px",
                                                maxWidth: "110px",
                                                width: "110px",
                                                justifyContent: "flex-end",
                                                flexShrink: 0
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                id="showOnlyMarked"
                                                checked={showOnlyMarked}
                                                onChange={e => setShowOnlyMarked(e.target.checked)}
                                                style={{ marginRight: 4 }}
                                            />
                                            <label htmlFor="showOnlyMarked" style={{ margin: 0, fontSize: "12px", userSelect: "none", cursor: "pointer" }}>
                                                Marcadas
                                            </label>
                                            <label htmlFor="showOnlyMarked" style={{ margin: "0 0 0 4px", fontSize: "12px", userSelect: "none", cursor: "pointer" }}>
                                                {"(" + filteredData.length + ")"}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData && filteredData.length > 0 ? (
                            filteredData.map((item, idx) => {
                                const isChecked = selected.includes(item[idField]);
                                // Exibe valor concatenado se concatFields for fornecido, senão exibe nameField
                                const displayValue = getDisplayValue(item, concatFields, nameField);
                                return (
                                    <tr
                                        key={item[idField]}
                                        onClick={e => {
                                            // Evita que o clique no checkbox duplique a ação
                                            if (e.target.type !== "checkbox") {
                                                handleRowClick(item[idField], isChecked);
                                            }
                                        }}
                                        style={{
                                            ...getRowStyle(idx),
                                            cursor: "pointer",
                                            userSelect: "none"
                                        }}
                                    >
                                        <td style={{ width: "40px", padding: "4px 8px", verticalAlign: "middle" }}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={e => handleSelectItem(item[idField], e.target.checked)}
                                                style={{ margin: 0 }}
                                                onClick={e => e.stopPropagation()} // Impede que o clique no checkbox propague para a linha
                                            />
                                        </td>
                                        <td style={{ padding: "4px 8px", verticalAlign: "middle" }}>
                                            {displayValue && displayValue.length > fieldLength
                                                ? displayValue.slice(0, fieldLength) + '...'
                                                : displayValue}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="2" style={{ padding: "8px", textAlign: "center" }}>
                                    <span className="text-muted">{emptyText}</span>
                                </td>
                            </tr>
                        )}
                        {/* Preencher linhas vazias para manter altura da tabela */}
                        {filteredData.length < visibleLines && Array.from({ length: visibleLines - filteredData.length }).map((_, idx) => (
                            <tr key={`empty-row-${idx}`}>
                                <td style={{ height: `${ROW_HEIGHT}px`, background: "transparent" }}></td>
                                <td style={{ background: "transparent" }}></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MultiSelect;